import React, { useState, useCallback, useEffect, useMemo } from 'react'
import useSWR from 'swr'
import Select from 'react-select'
import { api, updateVenueFeedMappings } from '../services/api'
import { Button, Banner, Panel, Badge } from '../components'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import styles from './FeedMapping.module.scss'

interface FeedSource {
  feed_id: string
  name: string
  primary_url?: string
  backup_url?: string
}

interface FeedOption {
  value: string
  label: string
}

interface Court {
  venue_id: string
  name: string
  city?: string
  country?: string
}

interface SeasonGroup {
  season_id: string
  name: string
  courts: Court[]
  start_date?: string | null
  end_date?: string | null
}

interface AssignmentWithDates {
  venueId: string
  courtName: string
  seasonName: string
  seasonId: string
  start_date: string | null
  end_date: string | null
}

interface Tournament {
  tournament_key: string
  name: string
  start_date: string
  end_date: string
  spans_multiple_weeks: boolean
  courts: Court[]
  seasons?: SeasonGroup[]
}

interface Week {
  week_index: number
  label: string
  start_date: string
  end_date: string
  tournaments: Tournament[]
}

const makeMappingKey = (venueId: string, seasonId: string) => `${venueId}::${seasonId}`
const parseMappingKey = (key: string) => {
  const [venueId, seasonId] = key.split('::')
  return { venueId, seasonId }
}

/** Two date ranges overlap if one starts before the other ends and ends after the other starts. */
function dateRangesOverlap(
  startA: string | null | undefined,
  endA: string | null | undefined,
  startB: string | null | undefined,
  endB: string | null | undefined
): boolean {
  if (!startA || !endA || !startB || !endB) return false
  return startA <= endB && endA >= startB
}

function formatDateRange(start: string | null, end: string | null): string {
  if (!start || !end) return ''
  const a = new Date(start)
  const b = new Date(end)
  const sameYear = a.getFullYear() === b.getFullYear()
  const fmtShort = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const fmtYear = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  if (start === end) return fmtYear(a)
  if (sameYear && a.getMonth() === b.getMonth()) return `${fmtShort(a)} – ${fmtShort(b)}, ${a.getFullYear()}`
  if (sameYear) return `${fmtShort(a)} – ${fmtShort(b)}, ${a.getFullYear()}`
  return `${fmtYear(a)} – ${fmtYear(b)}`
}

const FeedMapping: React.FC = () => {
  // courtMappings keyed on "venue_id::season_id" -> feed_id
  const [courtMappings, setCourtMappings] = useState<Record<string, string | null>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [collapsedWeeks, setCollapsedWeeks] = useState<Set<number>>(new Set([2, 3, 4]))
  const [summaryCollapsed, setSummaryCollapsed] = useState(true)
  const [expandedTournamentSections, setExpandedTournamentSections] = useState<Set<string>>(new Set())

  const { data: feedsData, isLoading: loadingFeeds, error: feedsError } = useSWR(
    'feed-sources',
    () => api.get('/publishing/feeds'),
    { revalidateOnFocus: false }
  )

  const { data: tournamentsData, isLoading: loadingTournaments, error: tournamentsError } = useSWR(
    'tournaments-by-week',
    () => api.get('/tournaments-by-week'),
    { revalidateOnFocus: false }
  )

  const { data: mappingsData, isLoading: loadingMappings, error: mappingsError, mutate: mutateMappings } = useSWR(
    'venue-feed-mappings',
    () => api.get('/publishing/venue-feed-mappings'),
    { revalidateOnFocus: false }
  )

  const feeds: FeedSource[] = useMemo(() => {
    return feedsData?.body ? JSON.parse(feedsData.body).feeds : feedsData?.feeds || []
  }, [feedsData])
  const weeks: Week[] = useMemo(() => {
    return tournamentsData?.body ? JSON.parse(tournamentsData.body).weeks : tournamentsData?.weeks || []
  }, [tournamentsData])

  useEffect(() => {
    if (!mappingsData) return
    const parsedMappings = mappingsData?.body ? JSON.parse(mappingsData.body).mappings : mappingsData?.mappings || []
    const mappingsMap: Record<string, string | null> = {}
    parsedMappings.forEach((mapping: any) => {
      if (mapping.venue_id && mapping.season_id && mapping.feed_id) {
        const key = makeMappingKey(mapping.venue_id, mapping.season_id)
        mappingsMap[key] = mapping.feed_id
      }
    })
    setCourtMappings(mappingsMap)
    setHasChanges(false)
  }, [mappingsData])

  const feedOptions: FeedOption[] = useMemo(() => {
    return feeds.map(feed => ({ value: feed.feed_id, label: feed.name }))
  }, [feeds])

  const getSelectedFeed = (venueId: string, seasonId: string): FeedOption | null => {
    const feedId = courtMappings[makeMappingKey(venueId, seasonId)]
    if (!feedId) return null
    const feed = feeds.find(f => f.feed_id === feedId)
    if (feed) return { value: feed.feed_id, label: feed.name }
    // Show current mapping even when feed is not in the list (e.g. removed feed)
    return { value: feedId, label: `Feed (${feedId})` }
  }

  /** Options for the feed Select; includes current value so the dropdown always shows the mapping. */
  const getFeedOptionsForCourt = (venueId: string, seasonId: string): FeedOption[] => {
    const selected = getSelectedFeed(venueId, seasonId)
    if (!selected) return feedOptions
    const inList = feedOptions.some(o => o.value === selected.value)
    return inList ? feedOptions : [selected, ...feedOptions]
  }

  const handleFeedChange = useCallback((venueId: string, seasonId: string, selectedOption: FeedOption | null) => {
    const key = makeMappingKey(venueId, seasonId)
    setCourtMappings(prev => ({ ...prev, [key]: selectedOption?.value ?? null }))
    setHasChanges(true)
    setSaveMessage(null)
  }, [])

  const handleRemoveAssignment = useCallback((venueId: string, seasonId: string) => {
    setCourtMappings(prev => ({ ...prev, [makeMappingKey(venueId, seasonId)]: null }))
    setHasChanges(true)
    setSaveMessage(null)
  }, [])

  const handleSave = async () => {
    setIsSaving(true)
    setSaveMessage(null)
    try {
      const mappingsArray = Object.entries(courtMappings)
        .filter((entry): entry is [string, string] => entry[1] !== null)
        .map(([key, feed_id]) => {
          const { venueId, seasonId } = parseMappingKey(key)
          return { feed_id, venue_id: venueId, season_id: seasonId }
        })
      await updateVenueFeedMappings(mappingsArray)
      await mutateMappings()
      setSaveMessage({ type: 'success', text: 'Feed mappings saved successfully!' })
      setHasChanges(false)
    } catch (error) {
      setSaveMessage({ type: 'error', text: `Failed to save: ${(error as Error).message}` })
    } finally {
      setIsSaving(false)
    }
  }

  const toggleWeekCollapse = (weekIndex: number) => {
    setCollapsedWeeks(prev => {
      const newSet = new Set(prev)
      newSet.has(weekIndex) ? newSet.delete(weekIndex) : newSet.add(weekIndex)
      return newSet
    })
  }

  const tournamentSectionKey = (weekIndex: number, tournamentKey: string) => `${weekIndex}::${tournamentKey}`

  const isTournamentSectionExpanded = (weekIndex: number, tournamentKey: string) =>
    expandedTournamentSections.has(tournamentSectionKey(weekIndex, tournamentKey))

  const setTournamentSectionExpanded = (weekIndex: number, tournamentKey: string, expanded: boolean) => {
    const key = tournamentSectionKey(weekIndex, tournamentKey)
    setExpandedTournamentSections(prev => {
      const newSet = new Set(prev)
      if (expanded) newSet.add(key)
      else newSet.delete(key)
      return newSet
    })
  }

  // Parse saved mappings for name lookups (the mappings API includes season/venue names)
  const parsedMappings = useMemo(() => {
    if (!mappingsData) return []
    const raw = mappingsData?.body ? JSON.parse(mappingsData.body).mappings : mappingsData?.mappings || []
    return raw as any[]
  }, [mappingsData])

  // Build read-only summary: feed -> list of assignments with dates
  const currentAssignments = useMemo(() => {
    const feedMap: Record<string, { feedName: string; assignments: AssignmentWithDates[] }> = {}

    feeds.forEach(feed => {
      feedMap[feed.feed_id] = { feedName: feed.name, assignments: [] }
    })

    const venueNames: Record<string, string> = {}
    const seasonNames: Record<string, string> = {}
    const seasonDates: Record<string, { start_date: string | null; end_date: string | null }> = {}

    // Names from the saved mappings API (covers all mapped seasons, even outside the week window)
    parsedMappings.forEach((m: any) => {
      if (m.season?.tc_season_id && m.season?.name) {
        seasonNames[m.season.tc_season_id] = m.season.name
      }
      if (m.venue?.tc_venue_id) {
        venueNames[m.venue.tc_venue_id] = m.venue.tc_name || m.venue.ext_name || 'Unknown Court'
      }
    })

    // Names from tournaments-by-week (preferred — more detailed, includes dates)
    weeks.forEach(week => {
      week.tournaments.forEach(tournament => {
        tournament.seasons?.forEach(season => {
          seasonNames[season.season_id] = season.name
          seasonDates[season.season_id] = {
            start_date: season.start_date ?? null,
            end_date: season.end_date ?? null,
          }
          season.courts.forEach(court => {
            venueNames[court.venue_id] = court.name
          })
        })
        tournament.courts.forEach(court => {
          venueNames[court.venue_id] = court.name
        })
      })
    })

    Object.entries(courtMappings).forEach(([key, feedId]) => {
      if (!feedId || !feedMap[feedId]) return
      const { venueId, seasonId } = parseMappingKey(key)
      const dates = seasonDates[seasonId] ?? { start_date: null, end_date: null }
      feedMap[feedId].assignments.push({
        venueId,
        courtName: venueNames[venueId] || 'Unknown Court',
        seasonName: seasonNames[seasonId] || 'Unknown Season',
        seasonId,
        start_date: dates.start_date,
        end_date: dates.end_date,
      })
    })

    return Object.entries(feedMap).map(([feedId, data]) => ({
      feedId,
      ...data,
      assignments: [...data.assignments].sort((a, b) => {
        const sa = a.start_date ?? ''
        const sb = b.start_date ?? ''
        if (!sa) return 1
        if (!sb) return -1
        return sa.localeCompare(sb)
      }),
    }))
  }, [feeds, courtMappings, weeks, parsedMappings])

  const assignmentsByFeedId = useMemo(() => {
    const map: Record<string, AssignmentWithDates[]> = {}
    currentAssignments.forEach(a => { map[a.feedId] = a.assignments })
    return map
  }, [currentAssignments])

  /** Assignments for a feed that overlap the given season date range (for dropdown context). */
  const getAssignmentsOverlapping = useCallback((
    feedId: string,
    contextStart: string | null | undefined,
    contextEnd: string | null | undefined
  ): AssignmentWithDates[] => {
    const list = assignmentsByFeedId[feedId] ?? []
    if (!contextStart || !contextEnd) return list
    return list.filter(a =>
      dateRangesOverlap(a.start_date, a.end_date, contextStart, contextEnd)
    )
  }, [assignmentsByFeedId])

  const mappedCount = currentAssignments.filter(a => a.assignments.length > 0).length
  const isLoading = loadingFeeds || loadingMappings || loadingTournaments
  const error = feedsError || mappingsError || tournamentsError

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading feed mapping data</h2>
          <p>Failed to fetch data: {(error as Error).message}</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading feed data...</h3>
          <p>Please wait while we fetch the feed mapping data.</p>
        </div>
      </div>
    )
  }

  const hasAnyTournaments = weeks.some(week => week.tournaments.length > 0)

  return (
    <div>
      <div className={pageStyles.pageHeader}>
        <h1>Feed to Court Mapping</h1>
        <p>
          Assign feeds to courts for upcoming tournaments. Each court is mapped per-season
          (e.g., ATP and WTA may use different feeds on the same court).
        </p>
      </div>

      {saveMessage && <Banner variant={saveMessage.type} message={saveMessage.text} />}

      {/* Current Feed Assignments Summary (read-only) */}
      <Panel padding="none">
        <Panel.Header
          variant="neutral"
          collapsible
          collapsed={summaryCollapsed}
          onCollapsedChange={setSummaryCollapsed}
          trailing={<span>{mappedCount} of {feeds.length} feeds assigned</span>}
        >
          <h2>Current Feed Assignments</h2>
        </Panel.Header>
        {!summaryCollapsed && (
          <Panel.Body>
            <table className={styles.assignmentTable}>
              <thead>
                <tr>
                  <th className={styles.assignmentColFeed}>Feed</th>
                  <th className={styles.assignmentColDate}>Date</th>
                  <th className={styles.assignmentColCourt}>Court</th>
                  <th className={styles.assignmentColSeason}>Season</th>
                  <th className={styles.assignmentColRemove} aria-label="Remove" />
                </tr>
              </thead>
              <tbody>
                {currentAssignments.flatMap(assignment => {
                  const n = assignment.assignments.length
                  if (n === 0) {
                    return [
                      <tr key={assignment.feedId}>
                        <td className={styles.assignmentColFeed}>
                          <span className={listTableStyles.primaryText}>{assignment.feedName}</span>
                        </td>
                        <td className={styles.assignmentColDate}>—</td>
                        <td className={styles.assignmentColCourt} colSpan={2}>
                          <span className={listTableStyles.secondaryText}>Not assigned</span>
                        </td>
                        <td className={styles.assignmentColRemove} />
                      </tr>,
                    ]
                  }
                  return assignment.assignments.map((a, i) => (
                    <tr key={`${assignment.feedId}-${a.seasonId}-${a.courtName}-${i}`}>
                      {i === 0 ? (
                        <td rowSpan={n} className={styles.assignmentColFeed} valign="top">
                          <span className={listTableStyles.primaryText}>{assignment.feedName}</span>
                        </td>
                      ) : null}
                      <td className={styles.assignmentColDate}>
                        {formatDateRange(a.start_date, a.end_date) || '—'}
                      </td>
                      <td className={styles.assignmentColCourt}>
                        <span className={listTableStyles.primaryText}>{a.courtName}</span>
                      </td>
                      <td className={styles.assignmentColSeason}>
                        <span className={listTableStyles.secondaryText}>{a.seasonName}</span>
                      </td>
                      <td className={styles.assignmentColRemove}>
                        <button
                          type="button"
                          className={styles.assignmentRemoveBtn}
                          onClick={() => handleRemoveAssignment(a.venueId, a.seasonId)}
                          title="Remove this assignment"
                          aria-label={`Remove ${a.courtName} (${a.seasonName})`}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))
                })}
              </tbody>
            </table>
          </Panel.Body>
        )}
      </Panel>

      {!hasAnyTournaments ? (
        <div className={listTableStyles.noResults}>
          <h3>No upcoming tournaments</h3>
          <p>There are no tournaments scheduled in the next 4 weeks.</p>
        </div>
      ) : (
        <>
          <div className={styles.weeksContainer}>
            {weeks.map(week => (
              <Panel key={week.week_index} padding="none">
                <Panel.Header
                  variant="primary"
                  collapsible
                  collapsed={collapsedWeeks.has(week.week_index)}
                  onCollapsedChange={() => toggleWeekCollapse(week.week_index)}
                  trailing={
                    <span>{week.tournaments.length} tournament{week.tournaments.length !== 1 ? 's' : ''}</span>
                  }
                >
                  <h2>{week.label}</h2>
                </Panel.Header>

                {!collapsedWeeks.has(week.week_index) && (
                  <Panel.Body>
                    {week.tournaments.length === 0 ? (
                      <div className={listTableStyles.noResults}>
                        <p>No tournaments starting this week</p>
                      </div>
                    ) : (
                      week.tournaments.map(tournament => (
                        <Panel key={tournament.tournament_key} padding="none">
                          <Panel.Header
                            variant="secondary"
                            size="sm"
                            collapsible
                            collapsed={!isTournamentSectionExpanded(week.week_index, tournament.tournament_key)}
                            onCollapsedChange={(collapsed) => setTournamentSectionExpanded(week.week_index, tournament.tournament_key, !collapsed)}
                            trailing={tournament.spans_multiple_weeks ? <Badge variant="warning">Spans multiple weeks</Badge> : undefined}
                          >
                            <h3>{tournament.name}</h3>
                          </Panel.Header>
                          {isTournamentSectionExpanded(week.week_index, tournament.tournament_key) && (
                            <Panel.Body variant="no-padding">
                              <div className={listTableStyles.fullBleed}>
                                {(tournament.seasons && tournament.seasons.length > 0) ? (
                                  tournament.seasons.map(season => (
                                    <div key={season.season_id}>
                                      {tournament.seasons!.length > 1 && (
                                        <div className={listTableStyles.tableHeader} style={{ gridTemplateColumns: '1fr', background: '#f8f9fa', fontSize: '0.9em' }}>
                                          <div>{season.name}</div>
                                        </div>
                                      )}
                                      <div className={`${listTableStyles.tableBody} ${listTableStyles.tableBodyDense}`}>
                                        {season.courts.map(court => (
                                          <div key={`${court.venue_id}-${season.season_id}`} className={`${listTableStyles.tableRow} ${styles.courtRow}`}>
                                            <div className={listTableStyles.cellPrimary}>
                                              <p className={listTableStyles.primaryText}>{court.name}</p>
                                              {court.city && (
                                                <p className={listTableStyles.secondaryText}>
                                                  {court.city}{court.country ? `, ${court.country}` : ''}
                                                </p>
                                              )}
                                            </div>
                                            <div className={listTableStyles.cellPrimary}>
                                              <Select<FeedOption, false>
                                                value={getSelectedFeed(court.venue_id, season.season_id)}
                                                onChange={(selected) => handleFeedChange(court.venue_id, season.season_id, selected)}
                                                options={getFeedOptionsForCourt(court.venue_id, season.season_id)}
                                                isClearable
                                                placeholder="Select feed..."
                                                className={selectStyles.reactSelect}
                                                classNamePrefix="react-select"
                                                noOptionsMessage={() => 'No feeds available'}
                                                menuPortalTarget={document.body}
                                                formatOptionLabel={(option) => {
                                                  const assignments = getAssignmentsOverlapping(
                                                    option.value,
                                                    season.start_date,
                                                    season.end_date
                                                  )
                                                  const hasAssignments = assignments.length > 0
                                                  return (
                                                    <div className={styles.feedOptionLabel}>
                                                      <span className={styles.feedOptionName}>{option.label}</span>
                                                      {hasAssignments && (
                                                        <span className={styles.feedOptionAssignments}>
                                                          {' — '}
                                                          {assignments.map((a, i) => (
                                                            <span key={i}>
                                                              {i > 0 && ', '}
                                                              {a.courtName} ({a.seasonName})
                                                            </span>
                                                          ))}
                                                        </span>
                                                      )}
                                                    </div>
                                                  )
                                                }}
                                                styles={{
                                                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                  menu: (base) => ({ ...base, minWidth: 450 }),
                                                }}
                                              />
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className={listTableStyles.tableBody}>
                                    <div className={listTableStyles.noResults}>
                                      <p>No season data available for this tournament</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </Panel.Body>
                          )}
                        </Panel>
                      ))
                    )}
                  </Panel.Body>
                )}
              </Panel>
            ))}
          </div>
        </>
      )}

      <div className={styles.saveSection}>
        <Button onClick={handleSave} variant="primary" size="large" disabled={isSaving || !hasChanges}>
          {isSaving ? 'Saving...' : 'Save Mappings'}
        </Button>
        {hasChanges && <span className={styles.unsavedChanges}>You have unsaved changes</span>}
      </div>
    </div>
  )
}

export default FeedMapping
